// eslint-disable-next-line import/no-named-as-default
import MdiIcon from "@mdi/react";
import { useCallback, useMemo, useRef, useState } from "react";
import DataGrid, { HeaderRendererProps, SortableHeaderCell } from "react-data-grid";
import { useSelector } from "react-redux";
import AutoSizer from "react-virtualized-auto-sizer";

import { GetApp } from "@material-ui/icons";
import { mdiContentCopy, mdiHistory } from "@mdi/js";
import type { PaginationProps } from "antd";
import {
  Alert,
  Tooltip as AntTooltip,
  Button,
  Pagination,
  Popover,
  Typography
} from "antd";
import classnames from "classnames";
import { RootState } from "store/rootReducer";
import { selectMapExtent, useMapStore } from "stores/map";
import styled from "styled-components/macro";
import {
  isCommittedSelectionPolygon,
  polygonFilterToGeometry,
  unionExcludePolygons
} from "utils/polygonFilterGeometry";

import { useDebounce } from "hooks";
import { useRigFracTypeFilters } from "hooks/rigfrac/useRigFracTypeFilters";
import useUser from "hooks/useUser";

import { RigFracSort, RigFracSpatial, useRigFracPoints } from "api/rigfrac";

import { IColumns } from "models/columns";

import BusyIndicator from "components/data-table/BusyIndicator";
import CopyButton from "components/data-table/CopyButton";
import "components/data-table/DataTable.scss";
import { bboxFromExtentPolygon } from "components/map/utils/mapExtentBbox";
import Indicator from "components/ui/Indicator";

import { exportRigFracRows } from "./rigfracExport";
import { RIGFRAC_COLUMNS, RigFracColumnKey, getColumnDef } from "./types";

// react-data-grid's base `rdg` theme is dark; `rdg-light` matches the real Data Table.
const rdgThemeClassname = "rdg-light";

// Matches the Data Table's Header-group green (`#00C774`).
const RIGFRAC_HEADER_COLOR = "#00C774";

// Column chooser removed (product decision) — always show every column, in canonical order.
const ALL_COLUMN_KEYS: RigFracColumnKey[] = RIGFRAC_COLUMNS.map((col) => col.key);

const { Text } = Typography;

const DEFAULT_SORT: RigFracSort = { field: "activityDate", dir: "desc" };
const DEFAULT_PAGE_SIZE = 500;
const PAGE_SIZE_OPTIONS = ["100", "250", "500", "1000"];
const showTotal: PaginationProps["showTotal"] = (total) => `Total ${total} items`;

type GridRow = Record<string, string>;

/**
 * Standalone Rig/Frac Data Table widget. Spatially scoped by the committed
 * well polygon filter (Redux `state.filter`) that also drives the well
 * icons — viewport bbox by default, or the committed include polygon plus
 * any exclude polygons. Only reads `state.filter`, never dispatches into it.
 * Closest precedent: `geo-map/GeoMapDataTable/GeoMapDataTableWidget.tsx`.
 */
export function RigFracDataTableWidget(): JSX.Element {
  // Trial/readonly accounts get read-only access — no export, no column/cell copy.
  // Mirrors RigFracDataTable.tsx (the chart-overlay sibling) and the shared DataTable.
  const { isReadonly, hasTrialAccount } = useUser();
  const canExport = !isReadonly && !hasTrialAccount;

  // ── Widget-local state ──────────────────────────────────────────────────
  const [historical, setHistorical] = useState(false);
  const [sort, setSort] = useState<RigFracSort>(DEFAULT_SORT);
  const [localSort, setLocalSort] = useState<{
    key: RigFracColumnKey;
    dir: "asc" | "desc";
  } | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  // Per-column "Copied to Clipboard!" acknowledgement, keyed by column key
  // (mirrors DataTable.tsx's `showCopySuccessMessage`).
  const [copiedColumn, setCopiedColumn] = useState<Record<string, boolean>>({});

  // ── Spatial input ────────────────────────────────────────────────────────
  // Read-only from mapStore/Redux — this widget never dispatches into either.
  const mapExtent = useMapStore(selectMapExtent);
  // Debounced so panning/zooming doesn't fire a fresh request every frame.
  const debouncedMapExtent = useDebounce(mapExtent, 300);
  const polygonFilter = useSelector((state: RootState) => state.filter.polygonFilter);
  const excludePolygonsFilter = useSelector(
    (state: RootState) => state.filter.excludePolygonsFilter
  );
  const hasIncludeSelection = isCommittedSelectionPolygon(polygonFilter);

  const spatial: Maybe<RigFracSpatial> = useMemo(() => {
    const excludePolygon = unionExcludePolygons(excludePolygonsFilter);

    if (hasIncludeSelection) {
      return {
        polygon: polygonFilterToGeometry(polygonFilter),
        ...(excludePolygon && { excludePolygon })
      };
    }

    const bbox = bboxFromExtentPolygon(debouncedMapExtent);
    if (!bbox) return undefined;
    return { bbox, ...(excludePolygon && { excludePolygon }) };
  }, [hasIncludeSelection, polygonFilter, excludePolygonsFilter, debouncedMapExtent]);

  const { rigFilters, fracFilters, filtersKey } = useRigFracTypeFilters();

  // ── Data ─────────────────────────────────────────────────────────────────
  // Treat a change to any of these as an implicit "go to page 1", derived
  // DURING RENDER rather than corrected afterward in an effect. An effect-based
  // reset leaves an intervening commit where the filter key is already new but
  // `page` is still old — and that pair is part of the React Query key, so a
  // request goes out for the old page against the new (possibly smaller)
  // result set before the reset lands. Depend on `filtersKey` (the debounced
  // string), never rigFilters/fracFilters directly, or this would fire on
  // every render.
  const resetDepsRef = useRef({ spatial, historical, sort, pageSize, filtersKey });
  const prevResetDeps = resetDepsRef.current;
  const resetDepsChanged =
    prevResetDeps.spatial !== spatial ||
    prevResetDeps.historical !== historical ||
    prevResetDeps.sort !== sort ||
    prevResetDeps.pageSize !== pageSize ||
    prevResetDeps.filtersKey !== filtersKey;
  if (resetDepsChanged) {
    resetDepsRef.current = { spatial, historical, sort, pageSize, filtersKey };
  }
  const effectivePage = resetDepsChanged ? 1 : page;
  if (resetDepsChanged && page !== 1) {
    setPage(1);
  }

  // Omit empty filter objects from the request entirely (rather than sending
  // `{}`) — data-svc-rs keys filter-observability off whether the field is
  // present at all, not whether it's empty.
  const rigFiltersForRequest = Object.keys(rigFilters).length ? rigFilters : undefined;
  const fracFiltersForRequest = Object.keys(fracFilters).length ? fracFilters : undefined;
  const hasFilterSelection = !!rigFiltersForRequest || !!fracFiltersForRequest;

  const { rows, total, isLoading, isFetching, isError, refetch } = useRigFracPoints({
    spatial,
    historical,
    // rigFilters/fracFilters are new siblings; `filters` stays the retained
    // flat fallback.
    filters: {},
    rigFilters: rigFiltersForRequest,
    fracFilters: fracFiltersForRequest,
    sort,
    pageSize,
    page: effectivePage
  });

  const displayRows = useMemo(() => {
    if (!localSort) return rows;
    const def = getColumnDef(localSort.key);
    const isNumeric =
      localSort.key === "activityCount" ||
      localSort.key === "lat" ||
      localSort.key === "lng";
    const dirMultiplier = localSort.dir === "asc" ? 1 : -1;
    return [...rows].sort((a, b) => {
      if (isNumeric) {
        return (Number(def.format(a)) - Number(def.format(b))) * dirMultiplier;
      }
      return def.format(a).localeCompare(def.format(b)) * dirMultiplier;
    });
  }, [rows, localSort]);

  const gridRows: GridRow[] = useMemo(
    () =>
      displayRows.map((row) => {
        const out: GridRow = { id: row.id };
        RIGFRAC_COLUMNS.forEach((def) => {
          out[def.key] = def.format(row);
        });
        return out;
      }),
    [displayRows]
  );

  // Cell click → copy that cell's value to the clipboard (DataTable parity).
  function handleCellClick(_: unknown, row: GridRow, column: { key: string }) {
    if (!canExport) return;
    const cellValue = row[column.key];
    if (cellValue === undefined) return;
    navigator.clipboard.writeText(cellValue).catch((err) => {
      // eslint-disable-next-line no-console -- surface copy failures for debugging
      console.error(`Failed to copy. ${err}`);
    });
  }

  // Header copy button → copy the whole column (header + every loaded row's
  // value), newline-joined, and flash a 2s "Copied!" ack (DataTable parity).
  const copyColumnContents = useCallback(
    (columnName: string, columnKey: string) => {
      const headerAndData = [columnName, ...gridRows.map((row) => row[columnKey])].join(
        "\n"
      );
      navigator.clipboard
        .writeText(headerAndData)
        .then(() => {
          setCopiedColumn((prev) => ({ ...prev, [columnKey]: true }));
          setTimeout(() => {
            setCopiedColumn((prev) => ({ ...prev, [columnKey]: false }));
          }, 2000);
        })
        .catch((err) => {
          // eslint-disable-next-line no-console -- surface copy failures for debugging
          console.error(`Failed to copy. ${err}`);
        });
    },
    [gridRows]
  );

  function handleSortColumnsChange(
    sortColumns: readonly { columnKey: string; direction: "ASC" | "DESC" }[]
  ) {
    if (sortColumns.length === 0) {
      setLocalSort(null);
      return;
    }
    const { columnKey, direction } = sortColumns[sortColumns.length - 1];
    const dir: "asc" | "desc" = direction === "ASC" ? "asc" : "desc";
    if (columnKey === "activityDate") {
      // Server sort is restricted to activityDate — changing it re-triggers the request.
      setSort({ field: "activityDate", dir });
      setLocalSort(null);
    } else {
      // Other columns sort client-side, over the currently loaded rows only.
      setLocalSort({ key: columnKey as RigFracColumnKey, dir });
    }
  }

  const rdgSortColumns = localSort
    ? [
        {
          columnKey: localSort.key,
          direction: localSort.dir === "asc" ? ("ASC" as const) : ("DESC" as const)
        }
      ]
    : [
        {
          columnKey: "activityDate",
          direction: sort.dir === "asc" ? ("ASC" as const) : ("DESC" as const)
        }
      ];

  const gridColumns = useMemo(
    () =>
      ALL_COLUMN_KEYS.map((key) => {
        const def = getColumnDef(key);
        return {
          key,
          name: def.name,
          width: def.width,
          resizable: true,
          sortable: true,
          // Not the shared `DraggableHeader` — its `position: fixed` label
          // escapes the cell in this widget's AutoSizer layout and renders blank.
          headerRenderer: (props: HeaderRendererProps<GridRow>) => (
            <SortableHeaderCell {...props}>
              <HeaderCell>
                <span
                  title={def.name}
                  style={{
                    flex: 1,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    textIndent: "5px",
                    color: RIGFRAC_HEADER_COLOR
                  }}>
                  {def.name}
                </span>
                {/* Copies the whole column; revealed on header-cell hover via
                    DataTable.scss's `.dt-copy-button` rule (imported above).
                    Hidden entirely for trial/readonly accounts. */}
                {canExport && (
                  <AntTooltip title="Copy Data">
                    <Popover
                      open={copiedColumn[key] || false}
                      trigger="click"
                      overlayClassName="overlay-no-padding"
                      content={<Alert type="success" message="Copied to Clipboard!" />}>
                      <HeaderCopyButton
                        className="dt-copy-button"
                        data-testid={`rigfrac-col-copy-${key}`}
                        aria-label={`Copy ${def.name} column`}
                        onClick={(event) => {
                          // Don't let the click bubble to the sort handler.
                          event.stopPropagation();
                          copyColumnContents(def.name, key);
                        }}>
                        <MdiIcon path={mdiContentCopy} size={0.9} />
                      </HeaderCopyButton>
                    </Popover>
                  </AntTooltip>
                )}
              </HeaderCell>
            </SortableHeaderCell>
          )
        };
      }),
    [copiedColumn, copyColumnContents, canExport]
  );

  // ── Copy (reused) / Export (client-side) ────────────────────────────────
  const copyColumns: IColumns[] = ALL_COLUMN_KEYS.map((key, idx) => {
    const def = getColumnDef(key);
    return {
      key: def.key,
      name: def.name,
      width: def.width,
      resizable: true,
      sortable: true,
      color: "",
      dataType: "Text",
      sortOrder: idx,
      formatter: () => null
    };
  });
  const copyRows = gridRows;

  const [exportOpen, setExportOpen] = useState(false);

  function handleExport(format: "CSV" | "Excel") {
    exportRigFracRows(displayRows, ALL_COLUMN_KEYS, format);
    setExportOpen(false);
  }

  const showLoading = isLoading;
  const showError = isError && !showLoading;
  const showEmpty = !showLoading && !showError && rows.length === 0;
  const showGrid = !showLoading && !showError && rows.length > 0;

  return (
    <div
      style={{ display: "flex", flexDirection: "column", height: "100%", width: "100%" }}>
      {/* Mirrors DataTable.tsx's `Toolbar` layout — left group grows/wraps, right group hugs the edge. */}
      <Toolbar>
        <ToolbarLeft>
          {hasIncludeSelection && (
            <span data-testid="rigfrac-lasso-active">
              <Text type="secondary">Showing results within the well selection area</Text>
            </span>
          )}
        </ToolbarLeft>

        <ToolbarActions data-testid="rigfrac-toolbar-actions">
          <AntTooltip title="Show all weekly rows instead of the latest snapshot">
            <HistoricalButton
              $active={historical}
              aria-pressed={historical}
              aria-label="Show historical rows"
              onClick={() => setHistorical(!historical)}
              data-testid="rigfrac-historical-toggle">
              <MdiIcon path={mdiHistory} size={1.2} />
            </HistoricalButton>
          </AntTooltip>

          {/* Mirrors DataTable's ExportButton markup, but uses this widget's own client-side exporter.
              Visual order (matches DataTableActions.tsx): … Export, Copy.
              Hidden entirely for trial/readonly accounts (CopyButton self-guards). */}
          {canExport && (
            <Popover
              overlayClassName="export-popover"
              placement="bottomLeft"
              trigger="click"
              open={exportOpen}
              onOpenChange={setExportOpen}
              content={
                <div className="export-container">
                  {total > displayRows.length && (
                    <span data-testid="rigfrac-export-scope-note">
                      <Text
                        type="secondary"
                        style={{
                          display: "block",
                          padding: "4px 8px",
                          whiteSpace: "nowrap"
                        }}>
                        Exports the {displayRows.length} loaded rows of {total}. Increase
                        the page size to export more.
                      </Text>
                    </span>
                  )}
                  <button
                    className="export-button"
                    data-testid="rigfrac-export-csv"
                    disabled={rows.length === 0}
                    onClick={() => handleExport("CSV")}>
                    <GetApp fontSize="large" />
                    <span className="button-text">Export to CSV (.csv)</span>
                  </button>
                  <button
                    className="export-button"
                    data-testid="rigfrac-export-excel"
                    disabled={rows.length === 0}
                    onClick={() => handleExport("Excel")}>
                    <GetApp fontSize="large" />
                    <span className="button-text">Export to Excel (.xlsx)</span>
                  </button>
                </div>
              }>
              <AntTooltip title="Export Data Table" mouseEnterDelay={0.6}>
                <ExportTriggerButton
                  aria-label="Export Data Table"
                  data-testid="rigfrac-export-trigger"
                  disabled={rows.length === 0}>
                  <GetApp fontSize="large" />
                </ExportTriggerButton>
              </AntTooltip>
            </Popover>
          )}

          <CopyButton
            rows={copyRows}
            columns={copyColumns}
            selectedProperties={ALL_COLUMN_KEYS}
          />
        </ToolbarActions>
      </Toolbar>

      <div style={{ flex: 1, position: "relative", minHeight: 0 }}>
        {showError && (
          <div style={{ padding: 24 }}>
            <Text type="danger">Failed to load rig/frac data. Please try again.</Text>{" "}
            <Button size="small" onClick={() => refetch()} data-testid="rigfrac-retry">
              Retry
            </Button>
          </div>
        )}

        {/* Closes the asymmetry with the chart's dedicated filter-aware empty
            state — filtering to nothing must not look identical to an empty
            viewport. */}
        {showEmpty && (
          <Indicator
            message={
              hasFilterSelection
                ? "No rig/frac rows match the current filter"
                : "No Data Available"
            }
          />
        )}

        {showGrid && (
          <AutoSizer>
            {({ width, height }) => (
              <StyledGrid
                className={classnames(rdgThemeClassname)}
                columns={gridColumns}
                rows={gridRows}
                rowKeyGetter={(row: GridRow) => row.id}
                sortColumns={rdgSortColumns}
                onSortColumnsChange={handleSortColumnsChange}
                onRowClick={handleCellClick}
                style={{ width: `${Number(width)}px`, height: `${Number(height)}px` }}
              />
            )}
          </AutoSizer>
        )}

        {/* Centered spinner + faint overlay, same as the real Data Table
            (initial load and background refetch alike). */}
        <BusyIndicator visible={isFetching} />
      </div>

      {/* Mirrors the real Data Table's pagination config, but driven by local page/pageSize state. */}
      {total > pageSize && (
        <PaginationWrapper data-testid="rigfrac-pagination">
          <Pagination
            current={effectivePage}
            pageSize={pageSize}
            pageSizeOptions={PAGE_SIZE_OPTIONS}
            size="small"
            total={total}
            showTotal={showTotal}
            showSizeChanger
            showQuickJumper
            onChange={(nextPage, nextPageSize) => {
              setPage(nextPage);
              setPageSize(nextPageSize);
            }}
          />
        </PaginationWrapper>
      )}
    </div>
  );
}

// Standalone flexbox footer (own layout, not Pagination.tsx's fixed-height grid `Wrapper`).
// Two equal flex spacers bracket the prev/page/next `<li>`s so page numbers land in the middle.
const PaginationWrapper = styled.div`
  --ant-primary-color: var(--color-primary);
  flex-shrink: 0;
  height: auto;
  display: flex;
  align-items: center;
  padding: 2px 8px;

  .ant-pagination.ant-pagination-mini {
    display: flex;
    align-items: center;
    width: 100%;

    .ant-pagination-total-text {
      flex: 1 1 0;
    }

    .ant-pagination-options {
      flex: 1 1 0;
      display: flex;
      justify-content: flex-end;
      margin-left: 0;
    }
  }
  .ant-pagination-total-text {
    white-space: nowrap;
  }
  .ant-pagination-item > a {
    text-decoration: none;
  }
  .ant-pagination-item-active {
    border-width: 1.5px;
  }
  .ant-pagination-item-active:hover {
    border-color: var(--color-primary-hover);
  }
  .ant-pagination-item-active:hover > a {
    color: var(--color-primary-hover);
  }

  .ant-pagination-options-quick-jumper {
    display: inline-flex;
    align-items: center;
    gap: 4px;

    input {
      order: 10;
    }
  }
`;

// Mirrors DataTable.tsx's `Toolbar`. Height is pinned to 48px to match the real
// table, whose toolbar row is `columnChooserHeight(32px) + 16px padding` —
// useColumnChooserSize observes the chooser and updateToolbarHeight adds the 16.
// TableContext seeds `toolbarHeight: 50`, but that only survives the frames
// before the chooser's ResizeObserver first fires, so 48 is the settled value.
// This widget has no chooser, so its 36px buttons + 16px padding would
// otherwise make the toolbar 52px and steal 4px from the grid.
const Toolbar = styled.div`
  width: 100%;
  height: 48px;
  box-sizing: border-box;
  flex-shrink: 0;
  display: grid;
  grid-template-columns: 1fr minmax(0, max-content);
  gap: 8px;
  align-items: center;
  padding: 8px;
`;

const ToolbarLeft = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  min-width: 0;
`;

// Mirrors DataTable.tsx's `ActionsContainer`.
const ToolbarActions = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
`;

// Mirrors DataTable.tsx's `StyledGrid` — highlights cells on hover.
const StyledGrid = styled(DataGrid)`
  overflow: hidden;

  &:hover {
    overflow: scroll;
  }

  .rdg-cell {
    &:hover {
      background-color: rgba(220, 233, 246, 1);
    }
  }

  /* Tints the header cell edge-to-edge. The green sits over the theme's OPAQUE
     header background (the same base the real Data Table uses) — a bare
     translucent tint let scrolled rows show through the sticky header. */
  .rdg-header-row .rdg-cell {
    background-color: var(--rdg-header-background-color);
    background-image: linear-gradient(
      ${RIGFRAC_HEADER_COLOR}20,
      ${RIGFRAC_HEADER_COLOR}20
    );
  }
`;

// Mirrors DataTable.tsx's `HeaderContainer` — anchors the absolute copy button.
const HeaderCell = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  position: relative;
`;

// Mirrors DataTable.tsx's header `CopyButton`. Hidden by default; the reveal on
// header-cell hover and the transparent background come from DataTable.scss's
// `.dt-copy-button` rules (imported above).
const HeaderCopyButton = styled(Button)`
  position: absolute;
  right: 0;
  margin-right: 8px;
  background-color: transparent;
  border: none;
  padding: 0;
  opacity: 0;
  transition: opacity 0.2s;
`;

// Mirrors DataTable.tsx / ExportButton.tsx's `ActionButton`.
const ExportTriggerButton = styled.button`
  width: 36px;
  height: 36px;
  display: grid;
  grid-template-columns: 36px max-content;
  align-items: center;
  justify-items: center;
  background: transparent;
  border: none;
  color: #a2aaad;
  font-weight: 500;
  padding: 0;
  margin: 0;
  transition: color 0.15s ease-out;
  overflow: hidden;
  pointer-events: all;
  cursor: pointer;
  flex-shrink: 0;

  &:hover {
    color: var(--color-primary);
  }

  &:disabled {
    color: #a2aaad;
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

// Same `ActionButton` sizing as `ExportTriggerButton`, plus an always-on "pressed" state when active.
const HistoricalButton = styled.button<{ $active: boolean }>`
  width: 36px;
  height: 36px;
  display: grid;
  grid-template-columns: 36px max-content;
  align-items: center;
  justify-items: center;
  background: ${(p) => (p.$active ? "var(--color-primary)" : "transparent")};
  border: none;
  border-radius: 4px;
  color: ${(p) => (p.$active ? "#fff" : "#a2aaad")};
  font-weight: 500;
  padding: 0;
  margin: 0;
  transition: color 0.15s ease-out, background-color 0.15s ease-out;
  overflow: hidden;
  pointer-events: all;
  cursor: pointer;
  flex-shrink: 0;

  &:hover {
    color: ${(p) => (p.$active ? "#fff" : "var(--color-primary)")};
  }
`;
